/**
 * BrokerLoginInstructions Component
 *
 * Displays step-by-step instructions for the broker login process
 * Shows security notes and additional information based on backend response
 */

import React from "react";
import {
  Security,
  Timer,
  Shield,
} from "@mui/icons-material";
import { Icon } from "../../../../components/ds";

const BrokerLoginInstructions = ({
  brokerName,
  loginInstructions,
  additionalParams,
  loginStatus = "pending",
  brokerId, // Add brokerId prop to generate redirect URL
}) => {
  // Debug logging
  console.log("BrokerLoginInstructions - Received props:", {
    brokerName,
    loginInstructions,
    additionalParams,
    loginStatus,
    brokerId,
  });
  const getStatusColor = () => {
    switch (loginStatus) {
      case "pending":
        return "text-blue-600 dark:text-blue-400";
      case "in-progress":
        return "text-orange-600 dark:text-orange-400";
      case "success":
        return "text-green-600 dark:text-green-400";
      case "error":
        return "text-red-600 dark:text-red-400";
      default:
        return "text-fg-2";
    }
  };

  const getStatusIcon = () => {
    switch (loginStatus) {
      case "pending":
        return <Icon name="external" className="w-5 h-5" />;
      case "in-progress":
        return <Timer className="w-5 h-5" />;
      case "success":
        return <Icon name="success" className="w-5 h-5" />;
      case "error":
        return <Icon name="info" className="w-5 h-5" />;
      default:
        return <Icon name="info" className="w-5 h-5" />;
    }
  };

  const getStatusText = () => {
    switch (loginStatus) {
      case "pending":
        return "Ready to begin login process";
      case "in-progress":
        return "Login in progress...";
      case "success":
        return "Login completed successfully";
      case "error":
        return "Login failed - please try again";
      default:
        return "Preparing login...";
    }
  };

  return (
    <div className="space-y-6">
      {/* Status Header */}
      <div className={`flex items-center gap-3 ${getStatusColor()}`}>
        {getStatusIcon()}
        <span className="font-medium">{getStatusText()}</span>
      </div>

      {/* Login Steps */}
      {loginInstructions?.steps && (
        <div>
          <h4 className="font-semibold text-fg-1 mb-4 flex items-center gap-2">
            <Icon name="info" className="w-4 h-4 text-blue-500" />
            Login Steps
          </h4>
          <div className="space-y-3">
            {loginInstructions.steps.map((step, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center text-sm font-medium">
                  {index + 1}
                </div>
                <p className="text-sm text-fg-2 leading-relaxed">
                  {step}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Security Notes */}
      {loginInstructions?.notes && (
        <div>
          <h4 className="font-semibold text-fg-1 mb-4 flex items-center gap-2">
            <Security className="w-4 h-4 text-green-500" />
            Security Information
          </h4>
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="space-y-2">
              {loginInstructions.notes.map((note, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Shield className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-green-800 dark:text-green-200">
                    {note}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Additional Parameters Info */}
      {additionalParams && (
        <div>
          <h4 className="font-semibold text-fg-1 mb-4 flex items-center gap-2">
            <Icon name="info" className="w-4 h-4 text-blue-500" />
            Session Details
          </h4>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="grid grid-cols-1 gap-3 text-sm">
              {additionalParams.apiVersion && (
                <div className="flex justify-between">
                  <span className="text-fg-2">
                    API Version:
                  </span>
                  <span className="font-medium text-fg-1">
                    {additionalParams.apiVersion}
                  </span>
                </div>
              )}
              {additionalParams.defaultSegment && (
                <div className="flex justify-between">
                  <span className="text-fg-2">
                    Default Segment:
                  </span>
                  <span className="font-medium text-fg-1">
                    {additionalParams.defaultSegment}
                  </span>
                </div>
              )}
              {additionalParams.tokenRefreshSeconds && (
                <div className="flex justify-between">
                  <span className="text-fg-2">
                    Session Duration:
                  </span>
                  <span className="font-medium text-fg-1">
                    {Math.floor(additionalParams.tokenRefreshSeconds / 3600)}{" "}
                    hours
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Troubleshooting Tips */}
      <div>
        <h4 className="font-semibold text-fg-1 mb-4 flex items-center gap-2">
          <Icon name="info" className="w-4 h-4 text-fg-2" />
          Troubleshooting
        </h4>
        <div className="space-y-2 text-sm text-fg-2">
          <p>
            • If the login window doesn't open, check your browser's popup
            blocker
          </p>
          <p>• Make sure you're using the correct {brokerName} credentials</p>
          <p>• Complete any 2FA/OTP verification in the login window</p>
          <p>• The login window will close automatically when complete</p>
        </div>
      </div>

      {/* Browser Support Notice */}
      <div className="bg-bg-3 border border-[var(--divider)] rounded-lg p-4">
        <h5 className="font-medium text-fg-1 mb-2">
          Browser Requirements
        </h5>
        <div className="text-sm text-fg-2 space-y-1">
          <p>• Enable JavaScript and cookies</p>
          <p>• Allow popups for this site</p>
          <p>• Use a modern browser (Chrome, Firefox, Safari, Edge)</p>
        </div>
      </div>
    </div>
  );
};

export default BrokerLoginInstructions;
